/**
 * Reusable transformation create/edit form.
 * Used both on the full edit page (/transformations/:id/edit) and in the settings side panel.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { transformationsService, CreateTransformationData } from '@/services/transformations';
import { hierarchyService } from '@/services/hierarchyService';
import { templateService } from '@/services/templateService';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Autocomplete, AutocompleteOption } from '@/components/ui/Autocomplete';
import toast from 'react-hot-toast';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import api from '@/services/api';
import { BuildingOfficeIcon, CogIcon, MapPinIcon, PlusIcon, XMarkIcon, UserIcon } from '@heroicons/react/24/outline';

export interface TransformationFormProps {
  /** When provided, form is in edit mode and loads this transformation */
  transformationId?: string;
  /** 'page' = full page layout (with title, card); 'panel' = minimal wrapper for side panel */
  mode?: 'page' | 'panel';
  /** Called after successful save when in panel mode (optional) */
  onSuccess?: () => void;
  /** Called when user cancels when in panel mode (optional) */
  onCancel?: () => void;
}

export const TransformationForm: React.FC<TransformationFormProps> = ({
  transformationId,
  mode = 'page',
  onSuccess,
  onCancel
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEditing = !!transformationId;
  const id = transformationId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<CreateTransformationData & { vision?: string; scope?: string }>({
    name: '',
    description: '',
    hierarchy_type: 'TRANSFORMATION',
    parent_id: undefined,
    is_template: false,
    template_id: undefined,
    industry: undefined,
    functional_area: undefined,
    transformation_type: undefined,
    complexity_level: undefined,
    estimated_duration_months: undefined,
    estimated_cost_range_min: undefined,
    estimated_cost_range_max: undefined,
    tags: [],
    objectives: [],
    expected_outcomes: [],
    cost_drivers: {},
    cost_calculation_formula: undefined,
    custom_assumptions: {},
    organizational_hierarchy_id: undefined,
    functional_hierarchy_id: undefined,
    geographic_hierarchy_id: undefined,
    conversion_status: 'NOT_CONVERTED',
    is_converted_to_project: false,
    status: 'PLANNING',
    start_date: undefined,
    end_date: undefined,
    budget: undefined,
    vision: undefined,
    scope: undefined,
    classification: 'Internal'
  });

  const [businessHierarchyOptions, setBusinessHierarchyOptions] = useState<AutocompleteOption[]>([]);
  const [functionalHierarchyOptions, setFunctionalHierarchyOptions] = useState<AutocompleteOption[]>([]);
  const [geographicHierarchyOptions, setGeographicHierarchyOptions] = useState<AutocompleteOption[]>([]);
  const [userOptions, setUserOptions] = useState<AutocompleteOption[]>([]);
  const [businessHierarchySearch, setBusinessHierarchySearch] = useState('');
  const [functionalHierarchySearch, setFunctionalHierarchySearch] = useState('');
  const [geographicHierarchySearch, setGeographicHierarchySearch] = useState('');
  const [managerSearch, setManagerSearch] = useState('');
  const [managerId, setManagerId] = useState<string>('');
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerId, setOwnerId] = useState<string>('');
  const [loadingHierarchies, setLoadingHierarchies] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [availableKPIs, setAvailableKPIs] = useState<any[]>([]);
  const [selectedKPIs, setSelectedKPIs] = useState<any[]>([]);

  useEffect(() => {
    if (isEditing && id) {
      loadTransformation();
    } else {
      if (user?.id) {
        setOwnerId(user.id);
        setOwnerSearch(`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '');
      }
      loadHierarchiesAndKPIs();
    }
  }, [id, user?.id]);

  const loadAllUsers = async (): Promise<AutocompleteOption[]> => {
    if (!user?.tenantId) return [];
    try {
      setLoadingUsers(true);
      const response = await api.get('/generic/users');
      const list: any[] = response.data?.data ?? [];
      const tenantId = user.tenantId;
      const filtered = list.filter(
        (u: any) => u.id && (!tenantId || u.tenant_id === tenantId)
      );
      const options = filtered.map((u: any) => ({
        id: u.id,
        label: `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim() || u.email || '',
        subtitle: u.email
      }));
      setUserOptions(options);
      return options;
    } catch (err) {
      console.error('Error loading users:', err);
      return [];
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    const searchTerm = managerSearch || ownerSearch;
    if (searchTerm.length === 0 && userOptions.length === 0 && user?.tenantId) {
      loadAllUsers();
    }
  }, [managerSearch, ownerSearch, user?.tenantId]);

  const loadHierarchiesAndKPIs = async () => {
    try {
      setLoadingHierarchies(true);
      const [businessHierarchy, functionalHierarchy, geographicHierarchy, kpisRes] = await Promise.all([
        hierarchyService.getBusinessHierarchyScoped().catch(() => []),
        hierarchyService.getFunctionalHierarchyScoped().catch(() => []),
        hierarchyService.getGeographicHierarchyScoped().catch(() => []),
        api.get('/kpis').catch(() => ({ data: [] }))
      ]);
      setBusinessHierarchyOptions(businessHierarchy.map(h => ({
        id: h.value,
        label: `${h.label.trim()} ${t('transformation.level')} ${h.level}`,
        subtitle: undefined
      })));
      setFunctionalHierarchyOptions(functionalHierarchy.map(h => ({
        id: h.value,
        label: `${h.label.trim()} ${t('transformation.level')} ${h.level}`,
        subtitle: undefined
      })));
      setGeographicHierarchyOptions(geographicHierarchy.map(h => ({
        id: h.value,
        label: `${h.label.trim()} ${t('transformation.level')} ${h.level}`,
        subtitle: undefined
      })));
      if (kpisRes.data && Array.isArray(kpisRes.data)) setAvailableKPIs(kpisRes.data);
    } catch (err) {
      console.error('Error loading hierarchies and KPIs:', err);
      toast.error(t('transformation.failedToLoadHierarchies'));
    } finally {
      setLoadingHierarchies(false);
    }
  };

  const loadTransformation = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const transformation = await transformationsService.getTransformation(id);
      await loadHierarchiesAndKPIs();
      const formatDateForInput = (date: string | Date | undefined): string | undefined => {
        if (!date) return undefined;
        if (typeof date === 'string') return date.includes('T') ? date.split('T')[0] : date;
        if (date instanceof Date) return date.toISOString().split('T')[0];
        return undefined;
      };
      setFormData({
        name: transformation.name || '',
        description: transformation.description || '',
        hierarchy_type: transformation.hierarchy_type,
        parent_id: transformation.parent_id,
        is_template: transformation.is_template || false,
        template_id: transformation.template_id,
        industry: transformation.industry,
        functional_area: transformation.functional_area,
        transformation_type: transformation.transformation_type,
        complexity_level: transformation.complexity_level,
        estimated_duration_months: transformation.estimated_duration_months,
        estimated_cost_range_min: transformation.estimated_cost_range_min,
        estimated_cost_range_max: transformation.estimated_cost_range_max,
        tags: transformation.tags || [],
        objectives: transformation.objectives || [],
        expected_outcomes: transformation.expected_outcomes || [],
        cost_drivers: transformation.cost_drivers || {},
        cost_calculation_formula: transformation.cost_calculation_formula,
        custom_assumptions: transformation.custom_assumptions || {},
        organizational_hierarchy_id: transformation.organizational_hierarchy_id,
        functional_hierarchy_id: transformation.functional_hierarchy_id,
        geographic_hierarchy_id: transformation.geographic_hierarchy_id,
        manager_id: transformation.manager_id,
        conversion_status: transformation.conversion_status || 'NOT_CONVERTED',
        is_converted_to_project: transformation.is_converted_to_project || false,
        status: transformation.status || 'PLANNING',
        start_date: formatDateForInput(transformation.start_date),
        end_date: formatDateForInput(transformation.end_date),
        budget: transformation.budget,
        vision: transformation.vision,
        scope: transformation.scope,
        classification: transformation.classification || 'Internal'
      });
      try {
        const loadedUserOptions = await loadAllUsers();
        if (transformation.owner_id) {
          setOwnerId(transformation.owner_id);
          const ownerOption = loadedUserOptions.find(u => u.id === transformation.owner_id);
          if (ownerOption) setOwnerSearch(ownerOption.label);
          else {
            try {
              const users = await templateService.searchUsers('a', user?.tenantId);
              const owner = users.find(u => u.id === transformation.owner_id);
              if (owner) {
                setOwnerSearch(`${owner.firstName || ''} ${owner.lastName || ''}`.trim() || owner.email || '');
                setUserOptions(prev => prev.find(u => u.id === owner.id) ? prev : [...prev, { id: owner.id, label: `${owner.firstName || ''} ${owner.lastName || ''}`.trim() || owner.email, subtitle: owner.email }]);
              } else setOwnerSearch('');
            } catch { setOwnerSearch(''); }
          }
        } else if (user?.id) {
          setOwnerId(user.id);
          setOwnerSearch(`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '');
        }
        if (transformation.manager_id) {
          setManagerId(transformation.manager_id);
          const managerOption = loadedUserOptions.find(u => u.id === transformation.manager_id);
          if (managerOption) setManagerSearch(managerOption.label);
          else {
            try {
              const users = await templateService.searchUsers('a', user?.tenantId);
              const manager = users.find(u => u.id === transformation.manager_id);
              if (manager) {
                setManagerSearch(`${manager.firstName || ''} ${manager.lastName || ''}`.trim() || manager.email || '');
                setUserOptions(prev => prev.find(u => u.id === manager.id) ? prev : [...prev, { id: manager.id, label: `${manager.firstName || ''} ${manager.lastName || ''}`.trim() || manager.email, subtitle: manager.email }]);
              } else setManagerSearch('');
            } catch { setManagerSearch(''); }
          }
        }
      } catch (err) {
        console.error('Error loading user data:', err);
      }
    } catch (err) {
      toast.error(t('transformation.failedToLoadTransformation'));
      if (mode === 'panel' && onCancel) onCancel();
      else navigate('/transformations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEditing && formData.organizational_hierarchy_id) {
      const orgHierarchy = businessHierarchyOptions.find(h => h.id === formData.organizational_hierarchy_id);
      if (orgHierarchy && businessHierarchySearch !== orgHierarchy.label) setBusinessHierarchySearch(orgHierarchy.label);
    }
  }, [businessHierarchyOptions, formData.organizational_hierarchy_id, isEditing]);
  useEffect(() => {
    if (isEditing && formData.functional_hierarchy_id) {
      const funcHierarchy = functionalHierarchyOptions.find(h => h.id === formData.functional_hierarchy_id);
      if (funcHierarchy && functionalHierarchySearch !== funcHierarchy.label) setFunctionalHierarchySearch(funcHierarchy.label);
    }
  }, [functionalHierarchyOptions, formData.functional_hierarchy_id, isEditing]);
  useEffect(() => {
    if (isEditing && formData.geographic_hierarchy_id) {
      const geoHierarchy = geographicHierarchyOptions.find(h => h.id === formData.geographic_hierarchy_id);
      if (geoHierarchy && geographicHierarchySearch !== geoHierarchy.label) setGeographicHierarchySearch(geoHierarchy.label);
    }
  }, [geographicHierarchyOptions, formData.geographic_hierarchy_id, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const submitData = {
        ...formData,
        owner_id: ownerId || user?.id || undefined,
        manager_id: managerId || undefined
      };
      if (isEditing) {
        await transformationsService.updateTransformation(id!, submitData);
        toast.success(t('transformation.transformationUpdated'));
      } else {
        await transformationsService.createTransformation(submitData);
        toast.success(t('transformation.transformationCreated'));
      }
      if (onSuccess) onSuccess();
      else navigate('/transformations');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message;
      toast.error(msg || (isEditing ? t('transformation.failedToUpdateTransformation') : t('transformation.failedToCreateTransformation')));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof CreateTransformationData, value: string | number | boolean | string[] | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value as any }));
  };
  const handleAddKPI = (kpi: any) => {
    if (!selectedKPIs.find(k => k.id === kpi.id)) setSelectedKPIs(prev => [...prev, { ...kpi, target_value: '', priority: 'MEDIUM' }]);
  };
  const handleRemoveKPI = (kpiId: string) => setSelectedKPIs(prev => prev.filter(k => k.id !== kpiId));
  const handleKPIChange = (kpiId: string, field: string, value: string) => {
    setSelectedKPIs(prev => prev.map(k => (k.id === kpiId ? { ...k, [field]: value } : k)));
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    else navigate('/transformations');
  };

  if (loading) {
    if (mode === 'panel') {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="text-sm text-[var(--color-secondary-text)]">{t('common.loading')}</div>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold text-[var(--color-surface-text)]">
          {isEditing ? (t('transformation.editProjectSettings') || 'Edit Project Settings') : t('transformation.create')}
        </h1>
        <div className="flex justify-center items-center h-64">
          <div className="text-[var(--color-secondary-text)]">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  const isPanel = mode === 'panel';
  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4 p-4">
      {/* Name, Hierarchy Type, Owner, Manager: one line each in panel */}
      <div className={isPanel ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-4 gap-4'}>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('common.name')} *</label>
          <Input type="text" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder={t('transformation.enterTransformationName')} required className="text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('transformation.hierarchyType')} *</label>
          <select value={formData.hierarchy_type} onChange={(e) => handleChange('hierarchy_type', e.target.value as 'TRANSFORMATION' | 'PROGRAM' | 'PROJECT' | 'SUB_PROJECT')} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]" required>
            <option value="TRANSFORMATION">{t('transformation.types.transformation')}</option>
            <option value="PROGRAM">{t('transformation.types.program')}</option>
            <option value="PROJECT">{t('transformation.types.project')}</option>
            <option value="SUB_PROJECT">{t('transformation.types.subProject')}</option>
          </select>
        </div>
        <div>
          <Autocomplete value={ownerId} onChange={setOwnerId} onSelect={(option) => { if (option) { setOwnerId(option.id); setOwnerSearch(option.label); } else { setOwnerId(''); setOwnerSearch(''); } }} options={userOptions} placeholder={t('transformation.searchForManager') || 'Search for owner...'} label={t('transformation.owner')} searchValue={ownerSearch} onSearchChange={setOwnerSearch} onFocus={() => { if (userOptions.length === 0 && user?.tenantId) loadAllUsers(); }} loading={loadingUsers} icon={<UserIcon className="h-4 w-4 text-[var(--color-secondary-text)]" />} />
        </div>
        <div>
          <Autocomplete value={managerId} onChange={setManagerId} onSelect={(option) => { if (option) { setManagerId(option.id); setManagerSearch(option.label); } else { setManagerId(''); setManagerSearch(''); } }} options={userOptions} placeholder={t('transformation.searchForManager')} label={t('transformation.managerOptional')} searchValue={managerSearch} onSearchChange={setManagerSearch} onFocus={() => { if (userOptions.length === 0 && user?.tenantId) loadAllUsers(); }} loading={loadingUsers} icon={<UserIcon className="h-4 w-4 text-[var(--color-secondary-text)]" />} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('common.description')} *</label>
        <textarea value={formData.description} onChange={(e) => handleChange('description', e.target.value)} placeholder={t('transformation.describeTransformation')} rows={2} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]" required />
      </div>
      {/* Vision, Objectives, Scope: one line each in panel */}
      <div className={isPanel ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-3 gap-4'}>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('transformation.vision')}</label>
          <textarea value={formData.vision || ''} onChange={(e) => handleChange('vision', e.target.value)} placeholder={t('transformation.describeVision')} rows={2} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('transformation.objectives')}</label>
          <textarea value={Array.isArray(formData.objectives) ? formData.objectives.join('\n') : formData.objectives || ''} onChange={(e) => handleChange('objectives', e.target.value.split('\n').filter(l => l.trim()))} placeholder={t('transformation.listKeyObjectives')} rows={2} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('transformation.scope')}</label>
          <textarea value={formData.scope || ''} onChange={(e) => handleChange('scope', e.target.value)} placeholder={t('transformation.defineScope')} rows={2} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]" />
        </div>
      </div>
      {/* Budget, Status, Classification */}
      <div className={isPanel ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-3 gap-4'}>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('common.budget')} *</label>
          <Input type="number" value={formData.budget || ''} onChange={(e) => handleChange('budget', parseFloat(e.target.value) || 0)} placeholder="0" min="0" step="0.01" required className="text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('common.status')}</label>
          <select value={formData.status} onChange={(e) => handleChange('status', e.target.value as 'PLANNING' | 'ACTIVE' | 'COMPLETED' | 'ON_HOLD' | 'CANCELLED')} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]">
            <option value="PLANNING">{t('transformation.planning')}</option>
            <option value="ACTIVE">{t('common.active')}</option>
            <option value="COMPLETED">{t('transformation.completed')}</option>
            <option value="ON_HOLD">{t('transformation.onHold')}</option>
            <option value="CANCELLED">{t('transformation.cancelled')}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('transformation.classification') || 'Sensitivity'}</label>
          <select value={formData.classification || 'Internal'} onChange={(e) => handleChange('classification', e.target.value)} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]">
            <option value="Public">Public</option>
            <option value="Internal">Internal</option>
            <option value="Confidential">Confidential</option>
            <option value="Secret">Secret</option>
            <option value="Top Secret">Top Secret</option>
          </select>
          <p className="text-xs text-[var(--color-secondary-text)] mt-0.5">{t('transformation.classificationHelp') || 'Who can see this project. You cannot set above your own clearance.'}</p>
        </div>
      </div>
      {/* Start date, End date: one line each in panel */}
      <div className={isPanel ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('transformation.startDate')} *</label>
          <Input type="date" value={formData.start_date || ''} onChange={(e) => handleChange('start_date', e.target.value)} required className="text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">{t('transformation.endDate')} *</label>
          <Input type="date" value={formData.end_date || ''} onChange={(e) => handleChange('end_date', e.target.value)} required className="text-sm" />
        </div>
      </div>
      {/* Hierarchies: one line each in panel */}
      <div className="pt-3 border-t border-[var(--color-border)]">
        <div className={isPanel ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-3 gap-4'}>
          <Autocomplete value={formData.organizational_hierarchy_id || ''} onChange={(value) => handleChange('organizational_hierarchy_id', value || undefined)} onSelect={(option) => { if (option) handleChange('organizational_hierarchy_id', option.id); else handleChange('organizational_hierarchy_id', undefined); }} options={businessHierarchyOptions} placeholder={t('transformation.searchBusinessHierarchy')} label={t('transformation.businessHierarchy')} searchValue={businessHierarchySearch} onSearchChange={setBusinessHierarchySearch} loading={loadingHierarchies} icon={<BuildingOfficeIcon className="h-4 w-4 text-[var(--color-secondary-text)]" />} />
          <Autocomplete value={formData.functional_hierarchy_id || ''} onChange={(value) => handleChange('functional_hierarchy_id', value || undefined)} onSelect={(option) => { if (option) handleChange('functional_hierarchy_id', option.id); else handleChange('functional_hierarchy_id', undefined); }} options={functionalHierarchyOptions} placeholder={t('transformation.searchFunctionalHierarchy')} label={t('transformation.functionalHierarchy')} searchValue={functionalHierarchySearch} onSearchChange={setFunctionalHierarchySearch} loading={loadingHierarchies} icon={<CogIcon className="h-4 w-4 text-[var(--color-secondary-text)]" />} />
          <Autocomplete value={formData.geographic_hierarchy_id || ''} onChange={(value) => handleChange('geographic_hierarchy_id', value || undefined)} onSelect={(option) => { if (option) handleChange('geographic_hierarchy_id', option.id); else handleChange('geographic_hierarchy_id', undefined); }} options={geographicHierarchyOptions} placeholder={t('transformation.searchGeographicHierarchy')} label={t('transformation.geographicHierarchy')} searchValue={geographicHierarchySearch} onSearchChange={setGeographicHierarchySearch} loading={loadingHierarchies} icon={<MapPinIcon className="h-4 w-4 text-[var(--color-secondary-text)]" />} />
        </div>
      </div>
      <div className="pt-3 border-t border-[var(--color-border)]">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs text-[var(--color-secondary-text)]">{selectedKPIs.length} {t('transformation.selected')}</div>
        </div>
        <div className="mb-3">
          <label className="block text-xs font-medium text-[var(--color-surface-text)] mb-1">{t('transformation.availableKPIs')}</label>
          <div className="max-h-24 overflow-y-auto border border-[var(--color-border)] rounded-md p-2 bg-[var(--color-surface-bg)]">
            {availableKPIs.length === 0 ? <div className="text-xs text-[var(--color-secondary-text)] py-1">{t('transformation.noKPIsAvailable')}</div> : availableKPIs.map(kpi => (
              <div key={kpi.id} className="flex items-center justify-between py-1 hover:bg-[var(--color-hover-bg)]">
                <div className="flex-1">
                  <div className="text-xs font-medium text-[var(--color-surface-text)]">{kpi.name}</div>
                  {kpi.unit ? (
                    <div className="text-xs text-[var(--color-secondary-text)]">{kpi.unit}</div>
                  ) : null}
                </div>
                <button type="button" onClick={() => handleAddKPI(kpi)} className="ml-2 p-1 text-[var(--color-info)] hover:bg-[var(--color-hover-bg)] rounded" disabled={!!selectedKPIs.find(k => k.id === kpi.id)}><PlusIcon className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        </div>
        {selectedKPIs.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-[var(--color-surface-text)] mb-1">{t('transformation.selectedKPIs')}</label>
            <div className="space-y-2">
              {selectedKPIs.map(kpi => (
                <div key={kpi.id} className="border border-[var(--color-border)] rounded-md p-2 bg-[var(--color-surface-bg)]">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex-1"><div className="text-xs font-medium text-[var(--color-surface-text)]">{kpi.name}</div></div>
                    <button type="button" onClick={() => handleRemoveKPI(kpi.id)} className="ml-2 p-1 text-[var(--color-error)] hover:bg-[var(--color-hover-bg)] rounded"><XMarkIcon className="h-3 w-3" /></button>
                  </div>
                  <div className={isPanel ? 'space-y-2' : 'grid grid-cols-2 gap-2'}>
                    <div>
                      <label className="block text-xs text-[var(--color-secondary-text)] mb-0.5">{t('transformation.target')}</label>
                      <input type="text" value={kpi.target_value} onChange={(e) => handleKPIChange(kpi.id, 'target_value', e.target.value)} className="w-full px-2 py-1 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]" placeholder={t('transformation.targetValue')} />
                    </div>
                    <div>
                      <label className="block text-xs text-[var(--color-secondary-text)] mb-0.5">{t('transformation.priority')}</label>
                      <select value={kpi.priority} onChange={(e) => handleKPIChange(kpi.id, 'priority', e.target.value)} className="w-full px-2 py-1 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]">
                        <option value="LOW">{t('transformation.priorityLow')}</option>
                        <option value="MEDIUM">{t('transformation.priorityMedium')}</option>
                        <option value="HIGH">{t('transformation.priorityHigh')}</option>
                        <option value="CRITICAL">{t('transformation.priorityCritical')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end space-x-3 pt-3 border-t border-[var(--color-border)]">
        <Button type="button" variant="outline" onClick={handleCancel} disabled={saving} size="sm">{t('common.cancel')}</Button>
        <Button type="submit" variant="primary" disabled={saving} size="sm">{saving ? t('common.saving') : (isEditing ? t('transformation.updateTransformation') : t('transformation.createTransformation'))}</Button>
      </div>
    </form>
  );

  if (mode === 'panel') {
    return <div className="pb-4">{formContent}</div>;
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="bg-[var(--color-surface-bg)] border-0 shadow-none p-4">
          <div className="mb-2">
            <h1 className="text-xl font-bold text-[var(--color-surface-text)] mb-1">
              {isEditing ? (t('transformation.editProjectSettings') || 'Edit Project Settings') : t('transformation.create')}
            </h1>
            <p className="text-xs text-[var(--color-secondary-text)]">
              {isEditing ? t('transformation.updateTransformationDetails') : t('transformation.defineTransformationInitiative')}
            </p>
          </div>
          <Card className="border-0 shadow-none bg-[var(--color-surface-bg)]">{formContent}</Card>
        </div>
      </div>
    </div>
  );
};
